import type { AdminCallEngineProcessing } from '@oracord/shared';

export interface EngineProcessingDisplaySection {
  title: string;
  items: string[];
}

export interface EngineProcessingDisplay {
  badge: 'Pending' | 'Processing' | 'Completed' | 'Failed';
  tone: 'slate' | 'amber' | 'emerald' | 'rose';
  message: string;
  sections: EngineProcessingDisplaySection[];
}

export function buildEngineProcessingDisplay(engine: AdminCallEngineProcessing): EngineProcessingDisplay {
  if (engine.status === 'failed') {
    return {
      badge: 'Failed',
      tone: 'rose',
      message: 'Engine processing failed',
      sections: [],
    };
  }

  if (engine.status === 'pending') {
    return {
      badge: 'Pending',
      tone: 'amber',
      message: 'Transcript is queued for the local Engine.',
      sections: [],
    };
  }

  if (engine.status === 'processing') {
    return {
      badge: 'Processing',
      tone: 'amber',
      message: 'Local Engine is processing this transcript.',
      sections: [],
    };
  }

  const result = engine.result;
  const sections: EngineProcessingDisplaySection[] = [];
  addTextSection(sections, 'Summary', result?.summary);
  addListSection(sections, 'Coaching angles', result?.coachingAngles);
  addListSection(sections, 'Action items', result?.actionItems);
  addListSection(sections, 'Follow-ups', result?.followUps);
  addListSection(sections, 'Tags', result?.tags);

  return {
    badge: 'Completed',
    tone: 'emerald',
    message: sections.length > 0
      ? 'Safe local Engine output. Raw transcript content is not shown here.'
      : 'Local Engine completed without displayable structured output.',
    sections,
  };
}

function addTextSection(sections: EngineProcessingDisplaySection[], title: string, text?: string): void {
  const trimmed = text?.trim();
  if (trimmed) {
    sections.push({ title, items: [trimmed] });
  }
}

function addListSection(sections: EngineProcessingDisplaySection[], title: string, items?: string[]): void {
  const safeItems = (items ?? []).map(item => item.trim()).filter(Boolean);
  if (safeItems.length > 0) {
    sections.push({ title, items: safeItems });
  }
}
